import { useEffect, useState } from 'react'

import { useConnect } from 'redux-bundler-hook'

import { AddRounded } from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from '@mui/material'

/**
 * @component
 * @param {Object} props - The props for the component.
 * @param {number[]} props.values
 * @param {Function} props.onChange
 * @param {boolean} props.disabled
 */
export default function HypotheticalRatesSelector({ values, onChange, disabled }) {
  const { doShowSnackbar } = useConnect('doShowSnackbar')

  const [inputValue, setInputValue] = useState('')
  const [inputPopoverAnchor, setInputPopoverAnchor] = useState(null)

  useEffect(() => {
    if (!inputPopoverAnchor) {
      setInputValue('')
    }
  }, [inputPopoverAnchor])

  const isError = (inputValue && inputValue < 1) || inputValue > 100

  return (
    <>
      <Popover
        open={!!inputPopoverAnchor}
        onClose={() => setInputPopoverAnchor(null)}
        anchorEl={inputPopoverAnchor}
        anchorOrigin={{ vertical: 36, horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box display="flex" gap={1} p={1}>
          <TextField
            error={isError}
            size="small"
            placeholder="1-100"
            disabled={disabled}
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              },
              htmlInput: {
                inputMode: 'numeric',
                maxLength: 3,
              },
            }}
            sx={{ width: 100 }}
          />
          <Button
            size="small"
            disabled={isError || !inputValue}
            onClick={() => {
              const numericValue = Number(inputValue)
              if (values.includes(numericValue)) {
                doShowSnackbar(`${numericValue}% rate already applied`, 'error')
              } else {
                onChange('add', numericValue)
                setInputPopoverAnchor(null)
              }
            }}
          >
            Submit
          </Button>
        </Box>
      </Popover>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        sx={{ maxWidth: 400, minWidth: 260, width: 1 }}
      >
        <Typography variant="body">Hypothetical Charge Rates</Typography>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            border: 1,
            borderRadius: 1,
            borderColor: '#e0e0e0',
            pl: values.length ? 1 : 2,
            pr: 1,
            py: 0.6,
            mt: 1,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width={1}
          >
            {values.length ? (
              <Box display="flex" alignItems="center" gap={0.5}>
                {values.map((value) => (
                  <Chip
                    key={value}
                    label={`${value}%`}
                    disabled={disabled}
                    size="small"
                    onDelete={() => onChange('delete', Number(value))}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="grey" sx={{ userSelect: 'none' }}>
                No charge rates applied
              </Typography>
            )}
            <IconButton
              size="small"
              disabled={disabled}
              onClick={(event) => setInputPopoverAnchor(event.currentTarget)}
            >
              <AddRounded color={disabled ? 'grey' : 'primary'} sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </>
  )
}
